import React, { useState } from "react";

import { useMutation } from "@tanstack/react-query";

import { revertPayoutApproval } from "services";

import { formatCurrencyForAdmin, formatNumber, useGetVerificationEditingRequests } from "utils";

import { CarbonProgram, EDITING_REQUEST_TYPE, EditingRequestType, Practice, VerificationEditingRequest, VerificationPointsPerCategory } from "models";

import { Col, InformationCard, InsightCard, Modal, Row, SkeletonWrapper, Typography, VerificationProgressCard, Message as message } from "components";

import { PayoutVerificationDrawer } from "pages/admin/verifications/components/drawers/PayoutVerificationDrawer";

type VerificationStatus = "approved" | "rejected" | "verifying" | "notVerified" | "partiallyApproved";

type VerificationsStatus = {
  completed: number;
  pending: number;
  autoVerified: number;
};

type PracticeVerificationsStatus = {
  verified: number;
  rejected: number;
  pending: number;
};

const calculatePointsSumFromCategory = (points: VerificationPointsPerCategory | undefined): number => {
  if (!points) return 0;
  const result = (points.removals || 0) + (points.avoidances || 0) + (points.reductions || 0);
  return Number.isNaN(result) ? 0 : result;
};

type PracticeStatusBySlug = {
  [practiceSlug: string]: {
    verified: number;
    rejected: number;
    pending: number;
  };
};

type FarmVerificationSummary = {
  verificationsStatus: VerificationsStatus;
  practiceVerificationsStatus: PracticeVerificationsStatus;
  practiceStatusBySlug?: PracticeStatusBySlug; // Practice-specific verification status
  practices: string[]; // Practice slugs
  points: VerificationPointsPerCategory;
  pointsRejected: VerificationPointsPerCategory;
  pointsRejectedSecondYearPractices: VerificationPointsPerCategory;
  pointsInitial?: VerificationPointsPerCategory; // Points at first submission (isFirstSubmission: true)
  vestingToBeLost?: number;
  adminVerificationPercentages?: Array<{ adminId: string; total: number }>;
};

type FieldVerificationSummary = {
  fieldPendingVerificationCount: number;
  fieldVerifiedVerificationCount: number;
  totalFieldVerificationCount: number;
  fieldApprovedVerifications?: number;
  fieldRejectedVerifications?: number;
};

type PracticeStep = {
  name: string;
  slug: string;
  status: VerificationStatus;
  // Legacy compatibility: VerificationProgressCard expects 'module' property for onClick
  module?: string;
};

interface FarmDashboardProps {
  farmId: string;
  farmIsVerified: boolean | null;
  verificationSummary: FarmVerificationSummary | undefined;
  fieldVerificationSummary: FieldVerificationSummary | undefined;
  carbonProgram: CarbonProgram | undefined;
  setActiveTab: (tab: string) => void;
  onUpdate: () => void;
  payoutApproved?: boolean;
}

const getFarmStatus = (verified: boolean | null): VerificationStatus => {
  if (verified) {
    return "approved";
  }

  if (verified === false) {
    return "rejected";
  }

  return "verifying";
};

const getFieldStatus = (farmStatus: VerificationStatus, fieldVerificationSummary: FieldVerificationSummary | undefined): VerificationStatus => {
  const { fieldPendingVerificationCount, fieldVerifiedVerificationCount, totalFieldVerificationCount } = fieldVerificationSummary || {};

  if (farmStatus === "approved") {
    if ((fieldPendingVerificationCount ?? 0) > 0) {
      return "verifying";
    }

    if (fieldVerifiedVerificationCount === totalFieldVerificationCount) {
      return "approved";
    }

    if ((fieldVerifiedVerificationCount ?? 0) > 0) {
      return "partiallyApproved";
    }

    return "rejected";
  }

  return "notVerified";
};

const getPracticeStatus = (
  farmStatus: VerificationStatus,
  fieldStatus: VerificationStatus,
  practiceSlug: string,
  verificationSummary: FarmVerificationSummary | undefined,
  fieldVerificationSummary: FieldVerificationSummary | undefined
): VerificationStatus => {
  // If farm is not approved, practices are not verified yet
  if (farmStatus !== "approved") {
    return "notVerified";
  }

  // If fields are not approved/partially approved, practices are not verified yet
  if (fieldStatus !== "approved" && fieldStatus !== "partiallyApproved") {
    return "notVerified";
  }

  // Check practice-specific status
  const practiceStatus = verificationSummary?.practiceStatusBySlug?.[practiceSlug];

  if (practiceStatus) {
    const totalItems = practiceStatus.verified + practiceStatus.pending + practiceStatus.rejected;

    // If this practice has pending items, it's being verified
    if (practiceStatus.pending > 0) {
      return "verifying";
    }

    // If this practice has verified items and no pending items, it's approved
    // (even if there are rejected items, as long as there are verified items and no pending)
    if (practiceStatus.verified > 0 && practiceStatus.pending === 0) {
      // If there are rejected items too, it's partially approved
      if (practiceStatus.rejected > 0) {
        return "partiallyApproved";
      }
      // Otherwise fully approved
      return "approved";
    }

    // If this practice has only rejected items (no verified, no pending), it's rejected
    if (practiceStatus.rejected > 0 && practiceStatus.verified === 0 && practiceStatus.pending === 0) {
      return "rejected";
    }

    // If this practice has no items at all, it's not verified
    if (totalItems === 0) {
      return "notVerified";
    }
  }

  // Fallback: check overall pending status
  const { pending: pendingItemsCountInFunction = 0 } = verificationSummary?.verificationsStatus || {};
  const hasPendingItems = pendingItemsCountInFunction > 0;
  const { fieldPendingVerificationCount: pendingFieldsCountInFunction = 0 } = fieldVerificationSummary || {};
  const hasPendingFields = pendingFieldsCountInFunction > 0;

  // If no pending items and fields are fully verified, practices are approved
  if (fieldStatus === "approved" && !hasPendingItems && !hasPendingFields) {
    return "approved";
  }

  // If fields are partially approved, practices are partially approved
  if (fieldStatus === "partiallyApproved") {
    return "partiallyApproved";
  }

  return "notVerified";
};

// Helper component for editing request insight cards with a per-practice breakdown
const EditingRequestCard = ({
  label,
  loading,
  fieldsCount,
  cropsCount,
  fertilizerCount,
  tillageSeedingCount,
  status,
}: {
  label: string;
  loading: boolean;
  fieldsCount: number;
  cropsCount: number;
  fertilizerCount: number;
  tillageSeedingCount: number;
  status?: "success" | "alert" | string;
}) => (
  <div className={`farm-stat ${status === "alert" ? "alert" : ""}`}>
    <div className="stats-column">
      <SkeletonWrapper isLoading={loading} type="button" buttonShape="round" buttonSize="small">
        <div className="stats">
          <Typography variant="h1" color={status ? (status === "success" ? "green-400" : "red-500") : undefined}>
            {fieldsCount + cropsCount + fertilizerCount + tillageSeedingCount}
          </Typography>
        </div>
      </SkeletonWrapper>
      <Typography variant="paragraph-m">{label}</Typography>
      {!loading && (
        <div className="margin-top-50">
          <Typography variant="paragraph-xs" color="grey-600">
            {fieldsCount} fields · {cropsCount} crops · {fertilizerCount} fertilizers · {tillageSeedingCount} tillage & seeding
          </Typography>
        </div>
      )}
    </div>
  </div>
);

const FERTILIZER_LEVEL_TYPES: EditingRequestType[] = [EDITING_REQUEST_TYPE.MINERAL_FERTILIZER, EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER];
const TILLAGE_SEEDING_LEVEL_TYPES: EditingRequestType[] = [EDITING_REQUEST_TYPE.TILLAGE, EDITING_REQUEST_TYPE.SEEDING];

// Helper function to calculate editing request counts
const calculateEditingRequestCounts = (editingRequests: VerificationEditingRequest[]) => {
  // Filter for field-related request types (shape, split, redzone)
  const fieldRequests = editingRequests.filter((request) => request.requestType.includes("field"));
  const cropRequests = editingRequests.filter((request) => request.requestType === EDITING_REQUEST_TYPE.CROP);
  const fertilizerRequests = editingRequests.filter((request) => FERTILIZER_LEVEL_TYPES.includes(request.requestType));
  const tillageSeedingRequests = editingRequests.filter((request) => TILLAGE_SEEDING_LEVEL_TYPES.includes(request.requestType));

  const bucketCounts = (requests: VerificationEditingRequest[]) => ({
    open: requests.filter(({ completedAt }) => !completedAt).length,
    completed: requests.filter(({ completedAt, reviewedAt }) => !!completedAt && !reviewedAt).length,
    reviewed: requests.filter(({ reviewedAt }) => !!reviewedAt).length,
  });

  const fieldCounts = bucketCounts(fieldRequests);
  const cropCounts = bucketCounts(cropRequests);
  const fertilizerCounts = bucketCounts(fertilizerRequests);
  const tillageSeedingCounts = bucketCounts(tillageSeedingRequests);

  return {
    open: { fields: fieldCounts.open, crops: cropCounts.open, fertilizer: fertilizerCounts.open, tillageSeeding: tillageSeedingCounts.open },
    completed: {
      fields: fieldCounts.completed,
      crops: cropCounts.completed,
      fertilizer: fertilizerCounts.completed,
      tillageSeeding: tillageSeedingCounts.completed,
    },
    reviewed: {
      fields: fieldCounts.reviewed,
      crops: cropCounts.reviewed,
      fertilizer: fertilizerCounts.reviewed,
      tillageSeeding: tillageSeedingCounts.reviewed,
    },
  };
};

export const FarmDashboard: React.FC<FarmDashboardProps> = ({
  farmId,
  farmIsVerified,
  verificationSummary,
  fieldVerificationSummary,
  carbonProgram,
  setActiveTab,
  onUpdate,
  payoutApproved = false,
}) => {
  // Note: We use carbonProgram.fieldsManagement.payouts for practice-based payout calculations
  // usePointPrice returns old module structure, so we don't use it here
  const [visible, setVisible] = useState<boolean>(false);
  const [showRevertModal, setShowRevertModal] = useState<boolean>(false);

  const { mutate: revertApproval, isPending: isReverting } = useMutation({
    mutationFn: () => revertPayoutApproval({ farmId }),
    onSuccess: () => {
      message({ content: "Payout approval reverted", icon: "tick" });
      setShowRevertModal(false);
      onUpdate();
    },
  });

  // Fetch editing requests for the farm
  const { data: editingRequests, isLoading: isLoadingEditingRequests } = useGetVerificationEditingRequests({ farmId, isAdmin: true });
  const editingRequestCounts = calculateEditingRequestCounts(editingRequests ?? []);

  // Get practices from carbon program
  const practices: Practice[] = carbonProgram?.fieldsManagement.practices || [];

  // Practice verification status
  const {
    rejected: rejectedPracticeCount = 0,
    pending: pendingPracticeCount = 0,
    verified: verifiedPracticeCount = 0,
  } = verificationSummary?.practiceVerificationsStatus || {};
  const showWarning = rejectedPracticeCount > 0;
  const hasFraudPotential =
    verificationSummary?.practiceVerificationsStatus && rejectedPracticeCount / (rejectedPracticeCount + pendingPracticeCount + verifiedPracticeCount) > 0.1;

  const farmStatus = getFarmStatus(farmIsVerified);
  const fieldStatus = getFieldStatus(farmStatus, fieldVerificationSummary);

  const pointsSum = calculatePointsSumFromCategory(verificationSummary?.points);
  // pointsRejectedSum represents credits rejected during verification
  const pointsRejectedSum = calculatePointsSumFromCategory(verificationSummary?.pointsRejected);

  const calculatePayoutFromCategory = (points: VerificationPointsPerCategory | undefined): number => {
    if (!carbonProgram?.fieldsManagement.payouts || !points) return 0;

    const removalsPrice = carbonProgram.fieldsManagement.payouts.removals?.pointPrice || 0;
    const avoidancesPrice = carbonProgram.fieldsManagement.payouts.avoidances?.pointPrice || 0;
    const reductionsPrice = carbonProgram.fieldsManagement.payouts.reductions?.pointPrice || 0;

    const result = (points.removals || 0) * removalsPrice + (points.avoidances || 0) * avoidancesPrice + (points.reductions || 0) * reductionsPrice;
    return Number.isNaN(result) ? 0 : result;
  };

  // Use pointsInitial (from first submission) for preVerification calculations
  // If pointsInitial is not available, fall back to points
  const initialPoints = verificationSummary?.pointsInitial || verificationSummary?.points;
  const initialPointsSum = calculatePointsSumFromCategory(initialPoints);

  // Show pre-verification values whenever initial points differ from latest points
  // This captures all changes: rejections, data updates, recalculations, etc.
  const preVerificationPracticePayout = verificationSummary && farmStatus !== "rejected" ? calculatePayoutFromCategory(initialPoints) : null;

  const preVerificationPracticeCredits = verificationSummary && farmStatus !== "rejected" ? initialPointsSum / 1000 : null;

  // Calculate actual payout and credits first to compare with preVerification values
  // Note: `points` already represents only the APPROVED points (what will be paid out).
  // `pointsRejected` is a separate category for rejected points (informational only).
  // These are mutually exclusive - don't subtract pointsRejected from points!
  const getActualPayout = (): number => {
    if (!verificationSummary) return 0;
    const practicePayout = calculatePayoutFromCategory(verificationSummary.points);
    const validPayout = Number.isNaN(practicePayout) || !Number.isFinite(practicePayout) ? 0 : practicePayout;

    return validPayout;
  };

  // Note: `pointsSum` already represents only the APPROVED points.
  // `pointsRejectedSum` is a separate category for rejected points.
  // These are mutually exclusive - don't subtract pointsRejectedSum from pointsSum!
  const getActualCredits = (): number => {
    if (!verificationSummary) return 0;
    const validPointsSum = Number.isNaN(pointsSum) || !Number.isFinite(pointsSum) ? 0 : pointsSum;

    const credits = validPointsSum / 1000;
    return Number.isNaN(credits) || !Number.isFinite(credits) ? 0 : credits;
  };

  const actualPayout = getActualPayout();
  const actualCredits = getActualCredits();

  // Calculate credits and payout lost due to rejections
  const creditsLostDueToRejections = pointsRejectedSum / 1000;
  const payoutLostDueToRejections = calculatePayoutFromCategory(verificationSummary?.pointsRejected);

  // Calculate credits and payout change due to data changes (changes not due to rejections)
  // dataChanges = initialPoints - currentApprovedPoints - rejectedPoints
  // Positive value = credits lost, Negative value = credits gained
  const creditsChangeDueToDataChanges = (initialPointsSum - pointsSum - pointsRejectedSum) / 1000;
  const initialPayout = calculatePayoutFromCategory(initialPoints);
  const payoutChangeDueToDataChanges = initialPayout - actualPayout - payoutLostDueToRejections;

  // Ensure preVerification values are valid numbers
  const validPreVerificationPayout =
    preVerificationPracticePayout === null || Number.isNaN(preVerificationPracticePayout) || !Number.isFinite(preVerificationPracticePayout)
      ? null
      : preVerificationPracticePayout;

  const validPreVerificationCredits =
    preVerificationPracticeCredits === null || Number.isNaN(preVerificationPracticeCredits) || !Number.isFinite(preVerificationPracticeCredits)
      ? null
      : preVerificationPracticeCredits;

  // Only show preVerification values if they're different from actual values
  const EPSILON = 0.01;
  const preVerificationPayout =
    validPreVerificationPayout !== null && Math.abs(validPreVerificationPayout - actualPayout) > EPSILON ? validPreVerificationPayout : null;
  const preVerificationCredits =
    validPreVerificationCredits !== null && Math.abs(validPreVerificationCredits - actualCredits) > EPSILON ? validPreVerificationCredits : null;

  const payout = verificationSummary ? actualPayout : null;
  const credits = verificationSummary ? actualCredits : null;

  const handleCompletePayoutApproval = (): void => {
    setVisible(false);
    setActiveTab("payout");
    onUpdate();
  };

  const handlePayoutDrawerClose = (): void => {
    setVisible(false);
  };

  const handleApproveClick = (): void => {
    if (payoutApproved) {
      setShowRevertModal(true);
    } else {
      setVisible(true);
    }
  };

  // Practice statuses are now determined per-practice using practiceStatusBySlug

  // Map practice slugs to tab keys for navigation
  const practiceSlugToTabKey: Record<string, string> = {
    crops: "crops",
    mineralFertilizers: "fertilizers",
    organicFertilizers: "fertilizers",
    tillages: "tillage-and-seeding",
    seedings: "tillage-and-seeding",
    plantProtections: "crops", // Plant protections are shown in crops tab
    landUseChanges: "fields", // Land use changes are shown in fields tab
  };

  // Map practices to the format expected by VerificationProgressCard
  const practiceSteps: PracticeStep[] = practices.map((practice) => {
    // Get status for this specific practice based on its slug
    const status = getPracticeStatus(farmStatus, fieldStatus, practice.slug, verificationSummary, fieldVerificationSummary);

    // Map practice slug to tab key for navigation
    const tabKey = practiceSlugToTabKey[practice.slug] || practice.slug;

    return {
      name: (practice as Practice & { name?: string }).name || practice.slug,
      slug: practice.slug,
      status,
      // Legacy compatibility: VerificationProgressCard onClick handler expects 'module' property
      // This maps to the tab key for navigation
      module: tabKey,
    };
  });

  return (
    <>
      {showWarning && (
        <Row className="margin-bottom-100 margin-top-100">
          <Col>
            <InformationCard
              iconName="tabler/file-search"
              title={hasFraudPotential ? "This farm has a fraud potential" : "Review this farm carefully"}
              text={
                hasFraudPotential
                  ? "This farm has more than 10% rejected verifications. Review rejected cases carefully and add more manually verified verifications to investigate if there is a bigger ratio of rejected verifications."
                  : "This farm has some rejected verifications. Review rejected cases carefully and add more manually verified elements to investigate if there is a bigger ratio of rejected verifications."
              }
              variant={hasFraudPotential ? "alert" : "neutral"}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={24} md={16} className="padding-right-25">
          <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-50 margin-top-100">
            Elements
          </Typography>
          <Row>
            <Col sm={6}>
              <InsightCard label="pending" loading={!verificationSummary} secondaryStat={`(${pendingPracticeCount})`}>
                {(() => {
                  const total = pendingPracticeCount + verifiedPracticeCount + rejectedPracticeCount;
                  const percentage = total > 0 ? (pendingPracticeCount / total) * 100 : 0;
                  return `${formatNumber({ number: percentage, decimalPlaces: 2 })}%`;
                })()}
              </InsightCard>
            </Col>
            <Col sm={6}>
              <InsightCard
                label="manually approved"
                loading={!verificationSummary || !fieldVerificationSummary}
                secondaryStat={(() => {
                  const { autoVerified = 0 } = verificationSummary?.verificationsStatus || {};
                  const {
                    totalFieldVerificationCount = 0,
                    fieldPendingVerificationCount = 0,
                    fieldApprovedVerifications = 0,
                    fieldRejectedVerifications = 0,
                  } = fieldVerificationSummary || {};
                  const autoVerifiedFields =
                    totalFieldVerificationCount - fieldPendingVerificationCount - fieldApprovedVerifications - fieldRejectedVerifications;
                  const autoApprovedPractices = Math.max(0, autoVerified - autoVerifiedFields);
                  const manuallyApprovedPractices = Math.max(0, verifiedPracticeCount - autoApprovedPractices);
                  return `(${manuallyApprovedPractices})`;
                })()}
              >
                {(() => {
                  const { autoVerified = 0 } = verificationSummary?.verificationsStatus || {};
                  const {
                    totalFieldVerificationCount = 0,
                    fieldPendingVerificationCount = 0,
                    fieldApprovedVerifications = 0,
                    fieldRejectedVerifications = 0,
                  } = fieldVerificationSummary || {};
                  const autoVerifiedFields =
                    totalFieldVerificationCount - fieldPendingVerificationCount - fieldApprovedVerifications - fieldRejectedVerifications;
                  const autoApprovedPractices = Math.max(0, autoVerified - autoVerifiedFields);
                  const manuallyApprovedPractices = Math.max(0, verifiedPracticeCount - autoApprovedPractices);
                  const total = pendingPracticeCount + verifiedPracticeCount + rejectedPracticeCount;
                  const percentage = total > 0 ? (manuallyApprovedPractices / total) * 100 : 0;
                  return `${formatNumber({ number: percentage, decimalPlaces: 2 })}%`;
                })()}
              </InsightCard>
            </Col>
            <Col sm={6}>
              <InsightCard
                label="auto-approved"
                loading={!verificationSummary || !fieldVerificationSummary}
                secondaryStat={(() => {
                  const { autoVerified = 0 } = verificationSummary?.verificationsStatus || {};
                  const {
                    totalFieldVerificationCount = 0,
                    fieldPendingVerificationCount = 0,
                    fieldApprovedVerifications = 0,
                    fieldRejectedVerifications = 0,
                  } = fieldVerificationSummary || {};
                  const autoVerifiedFields =
                    totalFieldVerificationCount - fieldPendingVerificationCount - fieldApprovedVerifications - fieldRejectedVerifications;
                  const autoApprovedPractices = Math.max(0, autoVerified - autoVerifiedFields);
                  return `(${autoApprovedPractices})`;
                })()}
              >
                {(() => {
                  const { autoVerified = 0 } = verificationSummary?.verificationsStatus || {};
                  const {
                    totalFieldVerificationCount = 0,
                    fieldPendingVerificationCount = 0,
                    fieldApprovedVerifications = 0,
                    fieldRejectedVerifications = 0,
                  } = fieldVerificationSummary || {};
                  const autoVerifiedFields =
                    totalFieldVerificationCount - fieldPendingVerificationCount - fieldApprovedVerifications - fieldRejectedVerifications;
                  const autoApprovedPractices = Math.max(0, autoVerified - autoVerifiedFields);
                  const total = pendingPracticeCount + verifiedPracticeCount + rejectedPracticeCount;
                  const percentage = total > 0 ? (autoApprovedPractices / total) * 100 : 0;
                  return `${formatNumber({ number: percentage, decimalPlaces: 2 })}%`;
                })()}
              </InsightCard>
            </Col>
            <Col sm={6}>
              <InsightCard
                label="rejected"
                loading={!verificationSummary}
                status={hasFraudPotential ? "alert" : undefined}
                secondaryStat={`(${rejectedPracticeCount})`}
              >
                {(() => {
                  const total = pendingPracticeCount + verifiedPracticeCount + rejectedPracticeCount;
                  const percentage = total > 0 ? (rejectedPracticeCount / total) * 100 : 0;
                  return `${formatNumber({ number: percentage, decimalPlaces: 2 })}%`;
                })()}
              </InsightCard>
            </Col>
          </Row>

          <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-50 margin-top-100">
            Fields
          </Typography>
          <Row>
            <Col sm={8}>
              <InsightCard label="pending" loading={!fieldVerificationSummary}>
                {fieldVerificationSummary?.fieldPendingVerificationCount ?? 0}
              </InsightCard>
            </Col>
            <Col sm={8}>
              <InsightCard label="approved" loading={!fieldVerificationSummary}>
                {fieldVerificationSummary?.fieldApprovedVerifications ?? 0}
              </InsightCard>
            </Col>
            <Col sm={8}>
              <InsightCard label="rejected" loading={!fieldVerificationSummary}>
                {fieldVerificationSummary?.fieldRejectedVerifications ?? 0}
              </InsightCard>
            </Col>
          </Row>

          <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-50 margin-top-100">
            Credits
          </Typography>
          <Row>
            <Col sm={12}>
              <InsightCard
                label={creditsChangeDueToDataChanges >= 0 ? "lost due to data changes" : "gained due to data changes"}
                loading={!verificationSummary}
                secondaryStat={formatCurrencyForAdmin(
                  creditsChangeDueToDataChanges >= 0 ? -Math.abs(payoutChangeDueToDataChanges) : Math.abs(payoutChangeDueToDataChanges),
                  "exceptZero"
                )}
              >
                {formatNumber({ number: Math.abs(creditsChangeDueToDataChanges), decimalPlaces: 2 })}
              </InsightCard>
            </Col>
            <Col sm={12}>
              <InsightCard
                label={creditsLostDueToRejections >= 0 ? "lost due to rejections" : "gained due to rejections"}
                loading={!verificationSummary}
                secondaryStat={formatCurrencyForAdmin(
                  creditsLostDueToRejections >= 0 ? -Math.abs(payoutLostDueToRejections) : Math.abs(payoutLostDueToRejections),
                  "exceptZero"
                )}
              >
                {formatNumber({ number: Math.abs(creditsLostDueToRejections), decimalPlaces: 2 })}
              </InsightCard>
            </Col>
          </Row>

          <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-50 margin-top-100">
            Data Editing Requests
          </Typography>
          <Row>
            <Col sm={8}>
              <EditingRequestCard
                label="open"
                loading={isLoadingEditingRequests}
                fieldsCount={editingRequestCounts.open.fields}
                cropsCount={editingRequestCounts.open.crops}
                fertilizerCount={editingRequestCounts.open.fertilizer}
                tillageSeedingCount={editingRequestCounts.open.tillageSeeding}
              />
            </Col>
            <Col sm={8}>
              <EditingRequestCard
                label="to review"
                loading={isLoadingEditingRequests}
                fieldsCount={editingRequestCounts.completed.fields}
                cropsCount={editingRequestCounts.completed.crops}
                fertilizerCount={editingRequestCounts.completed.fertilizer}
                tillageSeedingCount={editingRequestCounts.completed.tillageSeeding}
              />
            </Col>
            <Col sm={8}>
              <EditingRequestCard
                label="reviewed"
                loading={isLoadingEditingRequests}
                fieldsCount={editingRequestCounts.reviewed.fields}
                cropsCount={editingRequestCounts.reviewed.crops}
                fertilizerCount={editingRequestCounts.reviewed.fertilizer}
                tillageSeedingCount={editingRequestCounts.reviewed.tillageSeeding}
                status={
                  (editingRequests?.length ?? 0) > 0 &&
                  editingRequestCounts.reviewed.fields +
                    editingRequestCounts.reviewed.crops +
                    editingRequestCounts.reviewed.fertilizer +
                    editingRequestCounts.reviewed.tillageSeeding ===
                    (editingRequests?.length ?? 0)
                    ? "success"
                    : ""
                }
              />
            </Col>
          </Row>
        </Col>

        <Col sm={24} md={8} className="padding-left-0">
          <SkeletonWrapper isLoading={!verificationSummary || practices?.length === 0} type="block" height="30rem">
            <VerificationProgressCard
              payout={payout}
              credits={credits}
              preVerificationPayout={preVerificationPayout}
              preVerificationCredits={preVerificationCredits}
              farmStatus={farmStatus}
              fieldStatus={fieldStatus}
              // Note: Legacy prop name 'modules' - component expects this but we're passing practice steps
              // @ts-expect-error - Legacy component expects different type structure
              modules={practiceSteps}
              onClick={setActiveTab}
              onCtaClick={handleApproveClick}
              payoutApproved={payoutApproved}
            />
          </SkeletonWrapper>
        </Col>
      </Row>
      <PayoutVerificationDrawer
        farmId={farmId}
        visible={visible}
        verificationSummary={verificationSummary}
        onComplete={() => handleCompletePayoutApproval()}
        onClose={() => handlePayoutDrawerClose()}
        farmIsVerified={farmIsVerified}
        payout={payout}
        credits={credits}
        preVerificationPayout={preVerificationPayout}
        preVerificationCredits={preVerificationCredits}
        editingRequestCounts={editingRequestCounts}
      />
      <Modal
        title="Revert payout approval"
        description="This farm has already been approved for payout. Are you sure you want to revert the approval?"
        showModal={showRevertModal}
        okText="Revert"
        loading={isReverting}
        onOk={() => revertApproval()}
        onCancel={() => setShowRevertModal(false)}
      />
    </>
  );
};
